import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWireValue } from '@forminator/react-wire'
import { user as storeUser } from '@store'
import useAuthFormViewModel from './AuthFormViewModel'
import { noop } from '@utils'
import cn from 'clsx'

import CircleSpinner from '@components/shared/CircleSpinner'
import AuthForm from './AuthForm'
import { MdCheck, MdClose } from 'react-icons/md'

import './SignupOrgFlow.css'
import { ToastContainer, Slide } from 'react-toastify'
import { Tooltip } from 'react-tooltip'

const FORM_TYPE_JOIN = 'join-org'
const FORM_TYPE_CREATE = 'create-org'

const SignupOrgFlow = () => {
    
    const navigate = useNavigate()
    
    const [formType, setFormType] = useState(FORM_TYPE_CREATE)
    
    const user = useWireValue(storeUser)
    
    const viewModel = useAuthFormViewModel()
    
    const validationIcon = useMemo(() => {
        // if the formType is FORM_TYPE_CREATE, then it's not good to have
        // an existing name, so shows an X for that and a Check for non-existing
        // when its FORM_TYPE_JOIN then its good to exist, so it shows
        // Check for existing and X for non-existing
        const failOnExist = formType === FORM_TYPE_CREATE
        
        return viewModel.newOrgExists === failOnExist
            ? <MdClose className="text-2xl text-red-400" />
            : <MdCheck className="text-2xl text-green-400" />
    }, [viewModel.orgSignupValue, viewModel.newOrgExists])
    
    const submitButtonText = useMemo(() => (
        viewModel.orgJoinRequests?.length > 0
            ? null
            : formType === FORM_TYPE_JOIN
                ? 'Request to Join Organization'
                : 'Create Organization'
    ), [viewModel.orgJoinRequests, formType])
    
    const cancelButtonText = useMemo(() => (
        viewModel.orgJoinRequests?.length > 0
            ? null
            : formType === FORM_TYPE_JOIN
                ? 'I want to create a new Organization'
                : 'I want to join an existing Organization'
    ), [viewModel.orgJoinRequests, formType])
    
    const inputHintText = useMemo(() => (
        formType === FORM_TYPE_JOIN
            ? 'Your organization name'
            : 'New organization name'
    ), [formType])
    
    const iconHintText = useMemo(() => {
        if (formType === FORM_TYPE_JOIN)
            return ''
        return viewModel.newOrgExists ? 'Name already taken' : 'Name is avaliable'
    }, [formType, viewModel.newOrgExists])
    
    const shouldDisableSubmit = useMemo(() => {
        if (formType === FORM_TYPE_CREATE)
            return viewModel.orgSignupValue === '' || viewModel.newOrgExists
        else
            return viewModel.orgSignupValue === '' || !viewModel.newOrgExists
    }, [formType, viewModel.orgSignupValue, viewModel.newOrgExists])
    
    const switchFormType = useCallback(e => {
        e.preventDefault()
        setFormType(
            formType === FORM_TYPE_JOIN
                ? FORM_TYPE_CREATE
                : FORM_TYPE_JOIN,
        )
        viewModel.setOrgSignupValue('')
    }, [formType, viewModel])
    
    const onSubmit = useMemo(() => (
        formType === FORM_TYPE_JOIN
            ? viewModel.onJoinOrganizationSubmit
            : viewModel.onCreateOrganizationSubmit
    ), [formType, viewModel])
    
    useEffect(() => {
        
        if (!viewModel.orgSignupValue?.length)
            return
        
        const t = setTimeout(() => viewModel.checkOrgExists(viewModel.orgSignupValue), 500)
        
        return () => clearTimeout(t)
        
    }, [viewModel.orgSignupValue, viewModel.onCreateOrganizationChange])
    
    useEffect(() => {
        
        if (user?.organizations?.length) {
            setTimeout(() => navigate('/'), 1000)
            return
        }
        
        if (!viewModel.orgJoinRequests)
            viewModel.fetchOrgJoinRequests()
        
    }, [viewModel.orgJoinRequests])
    
    if (!viewModel.orgJoinRequests) return (
        <div className="SignupOrgFlow">
            <CircleSpinner className="absolute-centered" />
        </div>
    )
    
    return (
        
        <div className="SignupOrgFlow">
            
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={true}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={false}
                draggable={false}
                pauseOnHover
                transition={Slide}
                theme="light"/>
            
            <div className="absolute inset-x-0 top-0 w-full p-2 text-right">
                <button
                    className="btn btn-link"
                    onClick={async () => {
                        await viewModel.signOut()
                        navigate('/signin')
                    }}>
                    Logout
                </button>
            </div>
            
            {/* <div><pre><code className="text-white">
                {JSON.stringify(viewModel.orgJoinRequests, null, 4)}
            </code></pre></div> */}
            
            <div className="max-w-screen-xl mx-auto">
                
                <AuthForm
                    viewModel={viewModel}
                    onSubmit={viewModel.orgJoinRequests?.length > 0 ? noop : onSubmit}
                    submitButtonText={submitButtonText}
                    submitButtonDisabled={shouldDisableSubmit}
                    cancelButtonText={cancelButtonText}
                    cancelButtonClass="btn-link"
                    onCancelButtonClick={switchFormType}>
                    
                    <div className="text-base">
                        <p>Welcome to Parse!</p>
                        {!viewModel.orgJoinRequests?.length && (<>
                            <p className="mt-3">
                                Before you can access the app, you must be a member of an organization.
                                You can ask an organization member to add you to their organization.
                            </p>
                            <p className="mt-3">
                                If you know the name of your organization, you can enter it here.
                            </p>
                            <p className="mt-3">
                                Alternatively, you can create a new organization and add members to it.
                            </p>
                        </>)}
                    </div>
                    
                    {!viewModel.orgJoinRequests?.length && (
                        <div className="flex items-center justify-between mt-4">
                            
                            <div className="relative pb-4 grow">
                                <input
                                    className={cn('input input-bordered w-full pr-12', {
                                        '!border-red-400': formType === FORM_TYPE_CREATE
                                            ? (viewModel.orgSignupValue?.length > 0 && viewModel.newOrgExists)
                                            : (viewModel.orgSignupValue?.length > 0 && !viewModel.newOrgExists),
                                        '!border-green-400': formType === FORM_TYPE_CREATE
                                            ? (viewModel.orgSignupValue?.length > 0 && !viewModel.newOrgExists)
                                            : (viewModel.orgSignupValue?.length > 0 && viewModel.newOrgExists),
                                    })}
                                    type="text"
                                    value={viewModel.orgSignupValue}
                                    placeholder={inputHintText}
                                    autoComplete="off"
                                    onChange={e => viewModel.setOrgSignupValue(e.target.value)} />
                                
                                {viewModel.orgSignupValue?.length > 0 && <div
                                    className="absolute transform -translate-y-1/2 right-2 top-1/2 pb-4"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content={iconHintText}>
                                    {validationIcon}
                                </div>}
                            
                            </div>
                        
                        </div>
                    )}
                    
                    {viewModel.orgJoinRequests?.length > 0 && (
                        <div className="mt-4">
                            <p>Your request to join the following organizations is pending.</p>
                            <ul className="my-4">
                                {viewModel.orgJoinRequests.map(it => (
                                    <li key={it.id}>
                                        &mdash; {it.organization.name}
                                    </li>
                                ))}
                            </ul>
                            <p>Contact the organization owner approve your requests.</p>
                        </div>
                    )}
                
                </AuthForm>
            
            </div>
            <Tooltip variant="info" className="max-w-[300px]" id="default-tip" />
        </div>
        
    )
    
}

export default SignupOrgFlow
